import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import GenericForm from '../../components/GenericForm/GenericForm';
import Spinner from '../../components/Spinner/Spinner';
import styles from './Login.module.css'; //just for buttons width: 100% if changes to hold classes change the way it is imported
import { login as apiLogin, forgotPassword, resendConfirmationCode, confirmResetRequest, getSystemTables } from '../../services/ApiService';
import { useAuth } from '../../context/AuthContext';
import { ApiError, ErrorType } from '../../models/ApiError';
import { ErrorTypeHandler } from '../../utils/ErrorTypeHandler';
import { FormErrorHandler } from '../../utils/FormErrorHandler';
import { Snackbar, Alert } from '@mui/material';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    userName: '',
    password: '',
    newPassword: '',
    confirmationCode: '', // Added for forgot password functionality
  });

  const [errors, setErrors] = useState<{
    userName: string;
    password: string;
    newPassword: string;
    confirmationCode: string;
  }>({
    userName: '',
    password: '',
    newPassword: '',
    confirmationCode: '',
  });

  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  
  // Snackbar state for success/info messages
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const validatePassword = (password: string): string => {
    const rules = [
      { regex: /[A-Z]/, message: t('Login.Errors.Password.Uppercase') },
      { regex: /[a-z]/, message: t('Login.Errors.Password.Lowercase') },
      { regex: /\d/, message: t('Login.Errors.Password.Number') },
      { regex: /[\W_]/, message: t('Login.Errors.Password.Symbol') },
      { regex: /.{8,}/, message: t('Login.Errors.Password.Length') },
    ];

    for (const rule of rules) {
      if (!rule.regex.test(password)) return rule.message;
    }

    return '';
  };

  const loginMutation = useMutation({
    mutationFn: async () => {
      return await apiLogin(formData.userName, formData.password, formData.newPassword);
    },
    onSuccess: async (data) => {
      console.log('Login success data:', data);
      
      // Check for NEW_PASSWORD_REQUIRED challenge first, before accessing tokens
      if (data.authResult?.ChallengeName === 'NEW_PASSWORD_REQUIRED' || 
          data.ChallengeName === 'NEW_PASSWORD_REQUIRED' ||
          data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('NEW_PASSWORD_REQUIRED challenge detected');
        setIsNewPasswordRequired(true);
        return; // Don't proceed with normal login flow
      }

      // Only access tokens if they exist (normal successful login)
      if (data.tokens && data.tokens.accessToken) {
        localStorage.setItem('accessToken', data.tokens.accessToken);
        localStorage.setItem('refreshToken', data.tokens.refreshToken);
        localStorage.setItem('userName', formData.userName);
        
        if (data['custom:role']) {
          localStorage.setItem('custom:role', data['custom:role']);
        }

        auth.setIsLoggedIn(true);

        try {
          await queryClient.prefetchQuery({
            queryKey: ['systemTables'],
            queryFn: getSystemTables,
          });
        } catch (error) {
          console.error('Error prefetching system tables:', error);
        }
        navigate('/');
      } else {
        console.error('Unexpected response structure:', data);
        setErrors((prev) => ({
          ...prev,
          password: t('Login.Errors.GenericError'),
        }));
      }
    },
    onError: (error: any) => {
      // Debug: Log the full error to understand its structure
      console.log('Login error received:', error);
      console.log('Error details:', error.details);
      console.log('Error message:', error.message);
      
      if (ApiError.isApiError(error)) {
        // Check if this is a NEW_PASSWORD_REQUIRED challenge (backend may return it as an error)
        // Check multiple possible locations where the challenge might be
        const checkForNewPasswordRequired = (obj: any): boolean => {
          if (!obj) return false;
          
          // Direct check
          if (obj.ChallengeName === 'NEW_PASSWORD_REQUIRED') return true;
          if (obj.challengeName === 'NEW_PASSWORD_REQUIRED') return true;
          
          // Nested checks
          if (obj.authResult?.ChallengeName === 'NEW_PASSWORD_REQUIRED') return true;
          if (obj.authResult?.challengeName === 'NEW_PASSWORD_REQUIRED') return true;
          
          // Check in originalError
          if (obj.originalError?.ChallengeName === 'NEW_PASSWORD_REQUIRED') return true;
          if (obj.originalError?.challengeName === 'NEW_PASSWORD_REQUIRED') return true;
          if (obj.originalError?.authResult?.ChallengeName === 'NEW_PASSWORD_REQUIRED') return true;
          
          return false;
        };
        
        if (checkForNewPasswordRequired(error.details) || 
            error.message?.includes('NEW_PASSWORD_REQUIRED')) {
          console.log('NEW_PASSWORD_REQUIRED detected, switching to new password mode');
          setIsNewPasswordRequired(true);
          return; // Don't show error, this is expected behavior
        }

        // Extract field-specific errors if available
        const fieldErrors = FormErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        } else {
          // Get user-friendly error message
          const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
          setErrors((prev) => ({
            ...prev,
            password: errorMessage,
          }));
        }

        // Handle specific error types
        if (error.type === ErrorType.AUTHENTICATION_ERROR) {
          const message = error.message.toLowerCase();
          if (message.includes('password reset required')) {
            setIsForgotPassword(true);
          }
        }
      } else {
        // Fallback for non-ApiError errors
        setErrors((prev) => ({
          ...prev,
          password: t('Login.Errors.GenericError'),
        }));
      }
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async () => {
      return await forgotPassword(formData.userName);
    },
    onSuccess: () => {
      setIsForgotPassword(true);
      setSnackbar({
        open: true,
        message: t('Login.Messages.ConfirmationCodeSent'),
        severity: 'success',
      });
    },
    onError: (error: any) => {
      if (ApiError.isApiError(error)) {
        const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
        
        // For rate limit errors, still allow proceeding to next step
        if (error.type === ErrorType.RATE_LIMIT_ERROR) {
          setIsForgotPassword(true);
          setSnackbar({
            open: true,
            message: t('Login.Messages.ConfirmationCodeSent'),
            severity: 'info',
          });
        } else {
          setErrors((prev) => ({
            ...prev,
            userName: errorMessage,
          }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          userName: t('Login.Errors.GenericError'),
        }));
      }
    },
  });

  const resendCodeMutation = useMutation({
    mutationFn: async () => {
      return await resendConfirmationCode(formData.userName);
    },
    onSuccess: () => {
      console.log('Confirmation code resent successfully.');
      setSnackbar({
        open: true,
        message: t('Login.Messages.ConfirmationCodeSent'),
        severity: 'success',
      });
    },
    onError: (error: any) => {
      if (ApiError.isApiError(error)) {
        const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
        setErrors((prev) => ({
          ...prev,
          userName: errorMessage,
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          userName: t('Login.Errors.GenericError'),
        }));
      }
    },
  });

  const confirmResetMutation = useMutation({
    mutationFn: async () => {
      return await confirmResetRequest(formData.userName, formData.confirmationCode, formData.newPassword);
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: t('Login.Messages.ResetSuccess'),
        severity: 'success',
      });
      setIsForgotPassword(false);
      setFormData({
        userName: '',
        password: '',
        newPassword: '',
        confirmationCode: '',
      });
    },
    onError: (error: any) => {
      if (ApiError.isApiError(error)) {
        // Extract field-specific errors
        const fieldErrors = FormErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        } else {
          const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
          setErrors((prev) => ({
            ...prev,
            confirmationCode: errorMessage,
          }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          confirmationCode: t('Login.Errors.GenericError'),
        }));
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      userName: '',
      password: '',
      newPassword: '',
      confirmationCode: '',
    };

    if (!formData.userName) {
      newErrors.userName = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.UserName') });
    }
    if (isNewPasswordRequired || isForgotPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.NewPassword') });
      } else {
        const passwordError = validatePassword(formData.newPassword);
        if (passwordError) newErrors.newPassword = passwordError;
      }
    }
    if (isForgotPassword && !formData.confirmationCode) {
      newErrors.confirmationCode = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.ConfirmationCode') });
    }
    if (!isForgotPassword && !formData.password) {
      newErrors.password = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.Password') });
    }

    if (Object.values(newErrors).some((err) => err)) {
      setErrors(newErrors);
      return;
    }

    if (isForgotPassword) {
      console.log('Forgot password submission:', formData);
      confirmResetMutation.mutate();
    } else {
      loginMutation.mutate();
    }
  };

  const handleForgotPassword = () => {
    if (!formData.userName) {
      setErrors((prev) => ({
        ...prev,
        userName: t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.UserName') }),
      }));
      return;
    }
    forgotPasswordMutation.mutate();
  };

  const handleResendCode = () => {
    resendCodeMutation.mutate();
  };

  const handleBack = () => {
    setIsForgotPassword(false);
    setErrors({
      userName: '',
      password: '',
      newPassword: '',
      confirmationCode: '',
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const inputs = isForgotPassword
    ? [
        {
          name: 'userName',
          label: t('Login.Labels.UserName'),
          type: 'text',
          value: formData.userName,
          error: errors.userName,
        },
        {
          name: 'confirmationCode',
          label: t('Login.Labels.ConfirmationCode'),
          type: 'text',
          value: formData.confirmationCode,
          error: errors.confirmationCode,
        },
        {
          name: 'newPassword',
          label: t('Login.Labels.NewPassword'),
          type: 'password',
          value: formData.newPassword,
          error: errors.newPassword,
        },
      ]
    : [
        {
          name: 'userName',
          label: t('Login.Labels.UserName'),
          type: 'text',
          value: formData.userName,
          error: errors.userName,
        },
        {
          name: 'password',
          label: t('Login.Labels.Password'),
          type: 'password',
          value: formData.password,
          error: errors.password,
        },
        ...(isNewPasswordRequired
          ? [
              {
                name: 'newPassword',
                label: t('Login.Labels.NewPassword'),
                type: 'password',
                value: formData.newPassword,
                error: errors.newPassword,
              },
            ]
          : []),
      ];

  return (
    <div className={styles.loginContainer}>
      {(loginMutation.isPending || forgotPasswordMutation.isPending || resendCodeMutation.isPending || confirmResetMutation.isPending) && <Spinner />}
      <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        {isForgotPassword
          ? t('Login.Titles.TitleForgotPassword')
          : isNewPasswordRequired
          ? t('Login.Titles.TitleNewPassword')
          : t('Login.Titles.Title')}
      </h2>
      <GenericForm
        inputs={inputs}
        onChange={handleInputChange}
        onSubmit={handleFormSubmit}
        submitLabel={(isForgotPassword || isNewPasswordRequired) ? t('General.Labels.Confirm') : t('Login.Titles.Title')}
      />
      {isForgotPassword ? (
        <>
          <button onClick={handleResendCode}>
            {t('Login.Labels.ResendConfirmationCode')}
          </button>
          <button onClick={handleBack}>
            {t('General.Labels.Back')}
          </button>
        </>
      ) : (
        <button onClick={handleForgotPassword}>
          {t('Login.Labels.ForgotPassword')}
        </button>
      )}

      {/* Snackbar for success/info messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Login;